import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Rating,
  Skeleton,
  Typography,
} from "@mui/material";
import NorthIcon from "@mui/icons-material/North";
import SouthIcon from "@mui/icons-material/South";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useEffect, useMemo, useRef, useState, type TouchEvent } from "react";
import { useTranslation } from "react-i18next";
import { EnqueueSnackbar } from "notistack";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import AlertMessages from "common/Alert/AlertMessages";
import FormDialog from "common/Modal/Modal";
import {
  CustomerRatingFeedbackItem,
  GetCustomerRatingResponse,
} from "subModule/src/services/analytics/interface";
import { useGetCustomerRatingService } from "subModule/src/services/analytics/useAnalytics";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetTitleRowSx,
} from "components/Analytics/dashboardWidgetLayout";

dayjs.extend(relativeTime);

interface CustomerRatingFeedbackProps {
  themes: any;
  enqueueSnackbar: EnqueueSnackbar;
  globalFilters: {
    stationId: any;
    range: any;
    allStation: boolean;
    businessId?: any;
  };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
  stationsLoaded: boolean;
}

const STAR_COLOR = "#E67E22";
const ACCENT_BORDER = "#F59E0B";

const ratingSx = {
  "& .MuiRating-iconFilled svg": { fill: STAR_COLOR },
  "& .MuiRating-iconEmpty svg": { fill: "#D1D5DB" },
  "& .MuiRating-iconHover svg": { fill: STAR_COLOR },
};

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

export default function CustomerRatingFeedback(
  props: CustomerRatingFeedbackProps,
) {
  const { t } = useTranslation();
  const firstRender = useRef(true);
  const [averageRating, setAverageRating] = useState(0);
  const [totalRatings, setTotalRatings] = useState(0);
  const [ratingChange, setRatingChange] = useState<number | null>(null);
  const [recentFeedback, setRecentFeedback] = useState<
    CustomerRatingFeedbackItem[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const businessId =
    props.globalFilters.businessId?.value ??
    props.globalFilters.businessId ??
    null;

  const { refetch: apiRefetch } = useGetCustomerRatingService({
    stationId: props.globalFilters.stationId?.value || null,
    range: props.globalFilters.range?.value,
    allStation: props.globalFilters.allStation,
    ...(businessId ? { businessId: Number(businessId) } : {}),
  });

  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: GetCustomerRatingResponse | unknown | any) => {
        if (res?.data?.success) {
          const payload = res?.data?.data || {};
          setAverageRating(Number(payload.averageRating || 0));
          setTotalRatings(Number(payload.totalRatings || 0));
          const change =
            payload.ratingChange ??
            payload.averageRatingChange ??
            payload.ratingDelta;
          setRatingChange(
            change === undefined || change === null ? null : Number(change),
          );
          setRecentFeedback(payload.recentFeedback || []);
          setActiveIndex(0);
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied) props.setGlobalFilterApplied(false);
      });
  };

  useEffect(() => {
    if (firstRender.current && props.stationsLoaded) {
      firstRender.current = false;
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stationsLoaded]);

  useEffect(() => {
    if (props.globalFilterApplied) {
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);

  const hasFeedback = recentFeedback.length > 0;
  const activeItem = recentFeedback[activeIndex] || null;

  const primaryColor = props.themes?.primary || "#2563EB";

  const goTo = (index: number) => {
    if (!recentFeedback.length) return;
    const next =
      ((index % recentFeedback.length) + recentFeedback.length) %
      recentFeedback.length;
    setActiveIndex(next);
  };

  const handleTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.changedTouches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current == null) return;
    const delta = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 40) return;
    goTo(activeIndex + (delta < 0 ? 1 : -1));
  };

  const changePositive = (ratingChange ?? 0) >= 0;

  const renderFeedbackCard = (
    item: CustomerRatingFeedbackItem,
    options?: { showSectionLabel?: boolean },
  ) => {
    const displayName = item.customerName?.trim() || t("anonymous");
    const initials = getInitials(displayName);
    const relative = item.submittedAt ? dayjs(item.submittedAt).fromNow() : "";
    const subtitle = item.nameMasked ? t("nameMasked") : null;

    return (
      <Box>
        {options?.showSectionLabel !== false && (
          <Box
            sx={{
              ...dashboardWidgetHeaderSx,
              mb: 1.5,
              // keep relative time on the same row when space allows
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Typography
              className="font-Saira"
              sx={{
                fontSize: { xs: 10, sm: 11 },
                fontWeight: { xs: 600, sm: 700 },
                letterSpacing: "0.06em",
                color: "#6B7280",
                textTransform: "uppercase",
                textAlign: "left",
              }}
            >
              {t("recentCustomerVoice")}
            </Typography>
            <Typography
              className="font-Saira"
              sx={{ fontSize: { xs: 11, sm: 12 }, color: "#9CA3AF", whiteSpace: "nowrap", textAlign: "left" }}
            >
              {relative}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            border: "1px solid #E5E7EB",
            borderLeft: `4px solid ${ACCENT_BORDER}`,
            borderRadius: "10px",
            px: 1.75,
            py: 1.5,
            backgroundColor: "#FFFFFF",
            boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
          }}
        >
          <Box className="flex items-start justify-between gap-2 mb-1.5">
            <Box sx={dashboardWidgetTitleRowSx}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: "#1E293B",
                  color: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  fontFamily: "Saira, sans-serif",
                  fontWeight: 700,
                  fontSize: 11,
                }}
              >
                {initials}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  className="font-Saira"
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#0F2744",
                    lineHeight: 1.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayName}
                </Typography>
                {subtitle ? (
                  <Typography
                    className="font-Saira"
                    sx={{ fontSize: 10, color: "#9CA3AF", lineHeight: 1.3 }}
                  >
                    {subtitle}
                  </Typography>
                ) : null}
              </Box>
            </Box>
            <Rating
              value={Number(item.rating || 0)}
              precision={0.5}
              readOnly
              size="small"
              sx={{ ...ratingSx, flexShrink: 0, mt: 0.25 }}
            />
          </Box>

          {item.comment ? (
            <Typography
              className="font-Saira"
              sx={{
                fontSize: 12,
                color: "#374151",
                lineHeight: 1.5,
                fontStyle: "italic",
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              “{item.comment}”
            </Typography>
          ) : null}
        </Box>
      </Box>
    );
  };

  const carouselDots = useMemo(
    () => recentFeedback.map((item) => item.id),
    [recentFeedback],
  );

  return (
    <>
      <Card
        sx={{
          boxShadow: "none",
          borderRadius: "8px",
          width: "100%",
          height: "100%",
          maxHeight: { xs: 360, md: 340 },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: "1px solid #E5E7EB",
          "& .MuiCardContent-root": {
            padding: "12px !important",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
            boxSizing: "border-box",
          },
        }}
      >
        <CardContent>
          {loading ? (
            <Box>
              <Skeleton
                animation="pulse"
                sx={{ backgroundColor: props?.themes?.secondary, mb: 1 }}
              />
              <Skeleton
                animation="pulse"
                sx={{ backgroundColor: props?.themes?.secondary, mb: 1 }}
              />
              <Skeleton
                animation="pulse"
                sx={{ backgroundColor: props?.themes?.secondary, mb: 1 }}
              />
              <Skeleton
                animation="pulse"
                sx={{ backgroundColor: props?.themes?.secondary }}
              />
            </Box>
          ) : (
            <>
              <Box
                className="flex items-start justify-between gap-2"
                sx={{ flexShrink: 0 }}
              >
                <Box>
                  <Box className="flex items-center gap-2 mb-0.5">
                    <Typography
                      className="font-Saira"
                      sx={{
                        fontSize: 35,
                        fontWeight: 700,
                        lineHeight: 1,
                        color: "#0F2744",
                      }}
                    >
                      {averageRating.toFixed(1)}
                    </Typography>
                    <Rating
                      value={averageRating}
                      precision={0.1}
                      readOnly
                      size="medium"
                      sx={ratingSx}
                    />
                  </Box>
                  <Typography
                    className="font-Saira"
                    sx={{ fontSize: 11, color: "#9CA3AF" }}
                  >
                    {t("basedOnReviews", {
                      count: totalRatings,
                    })}
                  </Typography>
                </Box>

                {ratingChange != null && !Number.isNaN(ratingChange) ? (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.35,
                      px: 1,
                      py: 0.4,
                      borderRadius: "999px",
                      backgroundColor: changePositive ? "#E8F8EE" : "#FEECEC",
                      color: changePositive ? "#0B7A45" : "#C0392B",
                      flexShrink: 0,
                    }}
                  >
                    {changePositive ? (
                      <NorthIcon sx={{ fontSize: 11 }} />
                    ) : (
                      <SouthIcon sx={{ fontSize: 11 }} />
                    )}
                    <Typography
                      className="font-Saira"
                      sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1 }}
                    >
                      {Math.abs(ratingChange).toFixed(1)}
                    </Typography>
                  </Box>
                ) : null}
              </Box>

              <Divider
                sx={{ my: 1.5, borderColor: "#EEF0F3", flexShrink: 0 }}
              />

              <Box
                className="flex items-center justify-between gap-2 mb-1.5"
                sx={{ flexShrink: 0 }}
              >
                <Typography
                  className="font-Saira"
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#0F2744",
                  }}
                >
                  {t("recentFeedback")}
                </Typography>
                {hasFeedback ? (
                  <Button
                    onClick={() => setViewAllOpen(true)}
                    className="font-Saira"
                    sx={{
                      textTransform: "none",
                      minWidth: 0,
                      p: 0,
                      color: primaryColor,
                      fontWeight: 600,
                      fontSize: 12,
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {t("viewAll")} →
                  </Button>
                ) : null}
              </Box>

              {!hasFeedback ? (
                <Box
                  className="flex justify-center flex-col py-6"
                  sx={{ flex: 1 }}
                >
                  <Typography
                    className="font-Saira text-center italic font-semibold"
                    sx={{ color: props.themes?.primary }}
                  >
                    {t("noDataAvailable")}
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      display: "flex",
                      alignItems: "stretch",
                      gap: 0.5,
                    }}
                  >
                    {carouselDots.length > 1 ? (
                      <IconButton
                        size="small"
                        aria-label="previous-feedback"
                        onClick={() => goTo(activeIndex - 1)}
                        sx={{
                          alignSelf: "center",
                          width: 28,
                          height: 28,
                          flexShrink: 0,
                          border: "1px solid #E5E7EB",
                          backgroundColor: "#FFFFFF",
                          color: "#374151",
                          "&:hover": {
                            backgroundColor: "#F3F4F6",
                          },
                        }}
                      >
                        <ChevronLeftIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    ) : null}

                    <Box sx={{ flex: 1, minWidth: 0, overflow: "auto" }}>
                      {activeItem ? renderFeedbackCard(activeItem) : null}
                    </Box>

                    {carouselDots.length > 1 ? (
                      <IconButton
                        size="small"
                        aria-label="next-feedback"
                        onClick={() => goTo(activeIndex + 1)}
                        sx={{
                          alignSelf: "center",
                          width: 28,
                          height: 28,
                          flexShrink: 0,
                          border: "1px solid #E5E7EB",
                          backgroundColor: "#FFFFFF",
                          color: "#374151",
                          "&:hover": {
                            backgroundColor: "#F3F4F6",
                          },
                        }}
                      >
                        <ChevronRightIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    ) : null}
                  </Box>

                  {carouselDots.length > 1 ? (
                    <Box
                      className="flex items-center justify-center gap-1.5"
                      sx={{ pt: 1.25, flexShrink: 0 }}
                    >
                      {carouselDots.map((id, index) => {
                        const isActive = index === activeIndex;
                        return (
                          <Box
                            key={id}
                            component="button"
                            type="button"
                            aria-label={`feedback-${index + 1}`}
                            onClick={() => setActiveIndex(index)}
                            sx={{
                              width: isActive ? 8 : 7,
                              height: isActive ? 8 : 7,
                              borderRadius: "50%",
                              border: "none",
                              p: 0,
                              cursor: "pointer",
                              backgroundColor: isActive
                                ? primaryColor
                                : "#D1D5DB",
                              transition: "background-color 0.2s ease",
                            }}
                          />
                        );
                      })}
                    </Box>
                  ) : null}
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {viewAllOpen && (
        <FormDialog
          open={viewAllOpen}
          onClose={() => setViewAllOpen(false)}
          title="recentFeedback"
          hideActions
          maxWidth="sm"
          fullWidth
          content={
            <Box
              sx={{
                maxHeight: 480,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                pr: 0.5,
              }}
            >
              {recentFeedback.map((item) => (
                <Box key={item.id}>
                  {renderFeedbackCard(item, { showSectionLabel: true })}
                </Box>
              ))}
            </Box>
          }
        />
      )}
    </>
  );
}
